import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Gamepad2, Trophy, Drama, PartyPopper, Palette, ShoppingBag, GraduationCap, Laugh, X, Check, Gift, Plus } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { games_host: Gamepad2, sports_host: Trophy, theatre_host: Drama, events_host: PartyPopper, gallery_host: Palette, showroom_host: ShoppingBag, workshop_host: GraduationCap, jokes_host: Laugh };

/**
 * Hostings as big photo buttons. Pressing one transforms the whole section into that world
 * and lists every sub-type you can host. `status(key)` → {on, free, label} and `onPick(key)` are optional (Studio).
 */
export function HostingExplorer({ status, onPick, compact = false, title = "Pick your Stage" }) {
  const [hostings, setHostings] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/live/formats").then(({ data }) => setHostings(data.hostings || [])).catch(() => {}); }, []);
  const cur = useMemo(() => hostings.find((h) => h.key === active), [hostings, active]);

  if (!hostings.length) return null;

  return (
    <div data-testid="hosting-explorer" className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0B0B0C]">
      <AnimatePresence>
        {cur && (
          <motion.div key={cur.key} data-testid="hosting-backdrop" initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}
            className="absolute inset-0 pointer-events-none">
            <img src={cur.image} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.8) 55%, #0B0B0C 100%)` }} />
            <div className="absolute inset-0 mix-blend-overlay opacity-50" style={{ background: `radial-gradient(60% 50% at 20% 10%, ${cur.accent}, transparent)` }} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative p-5 md:p-7">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] font-bold" style={{ color: cur?.accent || "#FF9F0A" }}>{cur ? cur.label : title}</div>
            <h3 className={`mt-1 font-black tracking-tight leading-tight ${compact ? "text-base" : "text-xl md:text-2xl"}`} data-testid="hosting-heading">
              {cur ? cur.hint : "Press a Stage to see everything you can host inside it."}
            </h3>
          </div>
          {cur && (
            <button data-testid="hosting-close" onClick={() => setActive(null)} className="shrink-0 inline-flex items-center gap-1 rounded-full border border-white/20 bg-black/40 px-3 py-1.5 text-xs text-white hover:bg-white hover:text-black transition-colors"><X className="h-3.5 w-3.5" /> All Stages</button>
          )}
        </div>

        <div className={`mt-5 grid gap-3 ${compact ? (cur ? "grid-cols-4" : "grid-cols-2") : cur ? "grid-cols-4 sm:grid-cols-8" : "grid-cols-2 sm:grid-cols-4"}`}>
          {hostings.map((h, i) => {
            const Icon = ICONS[h.key] || Gamepad2;
            const st = status?.(h.key);
            const on = active === h.key;
            return (
              <motion.button key={h.key} type="button" data-testid={`hosting-btn-${h.key}`} onClick={() => setActive(on ? null : h.key)}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} whileHover={{ y: -3 }} whileTap={{ scale: 0.97 }}
                className={`group relative overflow-hidden text-left rounded-2xl border transition-[border-color,box-shadow] ${on ? "border-white shadow-[0_0_0_2px_rgba(255,255,255,0.35)]" : "border-white/10 hover:border-white/40"} ${cur ? "aspect-square" : "aspect-[4/3]"}`}
                style={{ boxShadow: on ? `0 0 40px ${h.accent}66` : undefined }}>
                <img src={h.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.75) 100%)` }} />
                <div className="absolute inset-x-0 top-0 h-1" style={{ background: h.accent }} />
                <div className={`relative h-full flex flex-col justify-between ${cur ? "p-2" : "p-3"}`}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/20 bg-black/40 backdrop-blur"><Icon className="h-4 w-4" style={{ color: h.accent }} /></span>
                    {st?.on && (cur || compact
                      ? <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#34C759] text-black"><Check className="h-3 w-3" /></span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-[#34C759] px-2 py-0.5 text-[10px] font-bold text-black"><Check className="h-3 w-3" />{st.free ? "Free · yours" : "Active"}</span>)}
                  </div>
                  <div>
                    <div className={`font-black leading-tight ${cur ? "text-[11px]" : "text-base"}`}>{h.label.replace(" Host", "")}</div>
                    {!cur && <div className="text-[11px] text-white/70 mt-0.5">{h.formats.length} kinds to host</div>}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          {cur && (
            <motion.div key={cur.key + "-list"} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="mt-6" data-testid="hosting-subtypes">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-white/80"><span className="font-black text-white">{cur.formats.length}</span> kinds of {cur.label.replace(" Host", "").toLowerCase()} you can host — all included in this one Hosting.</div>
                {onPick && (() => {
                  const st = status?.(cur.key);
                  return (
                    <button data-testid={`hosting-pick-${cur.key}`} onClick={() => onPick(cur.key)}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${st?.on ? "border border-white/30 text-white hover:bg-white/10" : "text-black"}`}
                      style={st?.on ? undefined : { background: cur.accent }}>
                      {st?.on ? <><X className="h-4 w-4" /> Remove this Hosting</> : st?.label?.startsWith("Claim") ? <><Gift className="h-4 w-4" /> Claim as my free Hosting</> : <><Plus className="h-4 w-4" /> {st?.label || "Add this Hosting"}</>}
                    </button>
                  );
                })()}
              </div>
              <div className={`mt-3 grid gap-2 ${compact ? "grid-cols-1" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"}`}>
                {cur.formats.map((f, i) => (
                  <motion.div key={f.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.015, 0.5) }} data-testid={`hosting-format-${f.key}`}
                    className="rounded-xl border border-white/15 bg-black/45 backdrop-blur px-3 py-2 text-sm text-white hover:border-white/50 hover:bg-black/60 transition-colors">
                    <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle" style={{ background: cur.accent }} />{f.label}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
